import { SiteRoute } from '@journeyapps/package-types';
import * as React from 'react';
import { DOCS_CONTEXT, DOCS_NAVIGATION, DocsNavigationFile, DocsNavigationNode } from '@journeyapps/package-docs';
import { findContentByPathname, normalizeContentModules, normalizePermalink } from '../../shared/content';
import { NavigationTreeItem } from '../../../widgets/NavigationTreeWidget';
import { TreeListTone } from '../../../widgets/TreeListWidget';
import { getDocsNodePath } from './navigation';
import { CrumbPart } from '../../../widgets/CrumbWidget';

export interface DocsFrontmatter {
  description: string;
  permalink: string;
  title: string;
}

export interface DocsModule {
  default: () => React.ReactNode;
  frontmatter: DocsFrontmatter;
  tableOfContents?: Array<{
    depth: 2 | 3;
    text: string;
  }>;
}

export interface DocsPage extends DocsModule {
  frontmatter: DocsFrontmatter;
}

export { normalizePermalink };

export const DOCS_PAGES = normalizeContentModules(
  DOCS_CONTEXT.keys().map((key) => DOCS_CONTEXT(key) as DocsModule) as DocsPage[]
) as DocsPage[];

export { DOCS_NAVIGATION };

export const getDocsFirstPagePath = (): string => {
  const walk = (items: DocsNavigationNode[]): string | null => {
    for (const item of items) {
      if (item.to) {
        return item.to;
      }

      if (item.children?.length) {
        const nested = walk(item.children);
        if (nested) {
          return nested;
        }
      }
    }

    return null;
  };

  return walk(DOCS_NAVIGATION.items) ?? SiteRoute.DOCS;
};

export const findDocsPage = (pathname: string): DocsPage | undefined => {
  return findContentByPathname(DOCS_PAGES, pathname);
};

const hasActiveDescendant = (item: DocsNavigationNode, normalizedPathname: string): boolean => {
  if (item.to && normalizePermalink(item.to) === normalizedPathname) {
    return true;
  }

  return item.children?.some((child) => hasActiveDescendant(child, normalizedPathname)) ?? false;
};

export const getDocsBreadcrumbParts = (pathname: string): CrumbPart[] => {
  const normalized = normalizePermalink(pathname);

  const walk = (items: DocsNavigationNode[], trail: CrumbPart[]): CrumbPart[] | null => {
    for (const item of items) {
      const nextTrail = [...trail, { label: item.label, to: getDocsNodePath(item) }];

      if (item.to && normalizePermalink(item.to) === normalized) {
        return nextTrail;
      }

      if (item.children?.length) {
        const nested = walk(item.children, nextTrail);
        if (nested) {
          return nested;
        }
      }
    }

    return null;
  };

  return (walk(DOCS_NAVIGATION.items, []) ?? []).slice(0, -1);
};

export const buildDocsNavigationTree = (pathname: string): NavigationTreeItem[] => {
  const normalized = normalizePermalink(pathname);

  const mapItems = (items: DocsNavigationNode[], level = 0): NavigationTreeItem[] =>
    items.map((item) => {
      const hasChildren = !!item.children?.length;

      return {
        active: !hasChildren && !!item.to && normalizePermalink(item.to) === normalized,
        collapsible: hasChildren,
        children: hasChildren ? mapItems(item.children!, level + 1) : undefined,
        defaultExpanded: hasActiveDescendant(item, normalized),
        icon: item.icon,
        key: item.to ?? item.label,
        label: item.label,
        level,
        tag: item.tag,
        tagVersion: item.tagVersion,
        to: hasChildren ? undefined : item.to,
        tone: level === 0 ? TreeListTone.HEADING : hasChildren && !item.icon ? TreeListTone.GROUP : TreeListTone.DEFAULT
      };
    });

  return mapItems(DOCS_NAVIGATION.items);
};
